import React from 'react';
import { Cell, ColumnInstance } from 'react-table';
import { Link, useHistory } from 'react-router-dom';

import { Tooltip, TitleFilenameLabel } from '@trqk/components';
import { getUseUploadTrackTaskId } from '@trqk/utils';

import { getWorkDetailPath } from '../../../../../../utils';
import { useUploadTracks } from '../../../../../../hooks';

import { defaultColumnWidth, titleWidthOffset } from '../constants';

interface TitleColumn extends ColumnInstance<WorksTableRow> {
    updateMyData: TableDataUpdater;
}

/**
 * @typedef {object} TitleCellProps
 * @property {object} cell the react-table Cell instance for a TrqkSong
 * @property {row} row the react-table Row instance for a TrqkSong
 * data
 */
interface TitleCellProps {
    cell: Cell<WorksTableRow>;
    column: TitleColumn;
}

// Works that were matched to an audio file which was later deleted will not have an audio_id. That
// means we can assume that any work in the table that has an audio_id was not deleted.
const audioDeleted = false;

/**
 * Displays the title for the WorksTableRow. If an upload is in progress, also displays the
 * currently uploading filename and an X button to cancel the upload.
 * @param {TitleCellProps}
 */
export const TitleCell = ({ cell, column }: TitleCellProps): JSX.Element => {
    const width =
        ((column?.width as number) ?? defaultColumnWidth) - titleWidthOffset;
    const { updateMyData } = column;
    const trqkAudioId = cell?.value?.audio_id ?? null;
    const title = cell?.value?.title;
    const filename = cell?.value?.filename;
    const progress = cell?.value?.progress ?? null;
    const uploadingFilename = cell?.value?.uploading_filename ?? null;
    const trqkSongId = cell?.value?.song_id;
    const taskId = getUseUploadTrackTaskId(trqkSongId ?? '');
    const { cancel } = useUploadTracks();
    const {
        location: { search },
    } = useHistory();

    const message =
        title && filename ? `${title} / ${filename}` : title || filename;

    const trackDetailPath = getWorkDetailPath({
        audioDeleted,
        search,
        trqkSongId,
        trqkAudioId,
    });

    let content = null;

    if (progress !== null && progress < 100) {
        content = (
            <TitleFilenameLabel
                audioDeleted={audioDeleted}
                filename={filename}
                onCancelUpload={(): void => {
                    cancel(taskId);
                    updateMyData(trqkSongId, 'progress', null);
                    updateMyData(trqkSongId, 'uploading_filename', null);
                }}
                title={title}
                uploadingFilename={uploadingFilename}
                width={width}
                underline={false}
            />
        );
    } else {
        content = (
            <>
                {trackDetailPath && (
                    <Link to={trackDetailPath}>
                        <TitleFilenameLabel
                            audioDeleted={audioDeleted}
                            filename={filename}
                            title={title}
                            width={width}
                            underline={true}
                        />
                    </Link>
                )}
            </>
        );
    }

    return (
        <div className="flex flex-col justify-center leading-tight">
            <Tooltip message={message} position="right">
                {content}
            </Tooltip>
        </div>
    );
};
